import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CalendarDays, Filter, RotateCcw, Download, ChevronDown, FileText } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface OrdersFiltersProps {
  onFilterChange: (filters: any) => void;
  onExport?: (format: "csv" | "pdf") => void;
  onDateRangeChange?: (range: string) => void;
  onReset?: () => void;
  programs: { programName: string; programCode: string; id: number }[];
  merchants?: any[];
  searchType: string;
  setSearchType: (type: string) => void;
  searchTerm: string;
  onSearch: (term: string) => void;
  dateRange?: string;
}

const FILTER_CATEGORIES = [
  { label: "Status", value: "status", isArray: false },
  { label: "Program", value: "program", isArray: false },
  { label: "Merchant", value: "merchant", isArray: false },
];

const STATUS_OPTIONS = [
  { label: "Created", value: "CREATED" },
  { label: "Cash Payment Success", value: "CASH_PAYMENT_SUCCESS" },
  { label: "Points Redemption Success", value: "POINTS_REDEMPTION_SUCCESS" },
  { label: "Cash Payment Failure", value: "CASH_PAYMENT_FAILURE" },
  { label: "Points Redemption Failure", value: "POINTS_REDEMPTION_FAILURE" },
  { label: "Finalized", value: "FINALIZED" },
  { label: "Shipped", value: "SHIPPED" },
  { label: "Delivered", value: "DELIVERED" },
  { label: "Cancelled", value: "CANCELLED" },
];

export const OrdersFilters: React.FC<OrdersFiltersProps> = ({
  onFilterChange,
  onExport,
  onDateRangeChange,
  onReset,
  programs = [],
  merchants = [],
  searchType,
  setSearchType,
  searchTerm,
  onSearch,
  dateRange,
}) => {
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState("status");
  const [filterSelections, setFilterSelections] = useState<any>({ status: '', program: '', merchant: '' });
  const [pendingFilterSelections, setPendingFilterSelections] = useState<any>({ status: '', program: '', merchant: '' });
  const [searchInputs, setSearchInputs] = useState<any>({ status: "", program: "", merchant: "" });

  // Count of active filters
  const activeFilterCount = ['status', 'program', 'merchant'].reduce((acc, key) => {
    return acc + (filterSelections[key] ? 1 : 0);
  }, 0);

  useEffect(() => {
    if (filterPopoverOpen) {
      setPendingFilterSelections({ ...filterSelections });
    }
  }, [filterPopoverOpen]);

  const applyFilters = () => {
    setFilterSelections({ ...pendingFilterSelections });
    const filterPayload: any = { status: pendingFilterSelections.status };
    // Find selected program's id
    if (pendingFilterSelections.program) {
      const selectedProgram = programs.find((p: any) => p.programCode === pendingFilterSelections.program);
      if (selectedProgram && selectedProgram.id) {
        filterPayload.programVO = { id: selectedProgram.id };
      }
    }
    // Find selected merchant's id
    if (pendingFilterSelections.merchant) {
      const selectedMerchant = merchants.find((m: any) => m.merchantCode === pendingFilterSelections.merchant);
      if (selectedMerchant && selectedMerchant.id) {
        filterPayload.redemptionMerchantId = selectedMerchant.id;
      }
    }
    onFilterChange(filterPayload);
    setFilterPopoverOpen(false);
  };

  const handleOptionSelect = (category: string, value: string) => {
    setPendingFilterSelections((prev: any) => ({ ...prev, [category]: prev[category] === value ? '' : value }));
  };

  const handleSearchInput = (category: string, value: string) => {
    setSearchInputs((prev: any) => ({ ...prev, [category]: value }));
  };

  const handleClearAll = () => {
    setFilterSelections({ status: '', program: '', merchant: '' });
    setPendingFilterSelections({ status: '', program: '', merchant: '' });
    setSearchInputs({ status: "", program: "", merchant: "" });
    setActiveCategory("status");
    onFilterChange({ status: '', programVO: null, redemptionMerchantId: null });
  };

  const handleClearCurrent = () => {
    if (activeCategory === "status") {
      setFilterSelections((prev: any) => ({ ...prev, status: '' }));
      onFilterChange({ ...filterSelections, status: '' });
    } else {
      setFilterSelections((prev: any) => ({ ...prev, [activeCategory]: '' }));
    }
    if (activeCategory !== 'status') {
      setSearchInputs((prev: any) => ({ ...prev, [activeCategory]: "" }));
    }
  };

  const renderOptions = () => {
    switch (activeCategory) {
      case "status":
        return (
          <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
            {STATUS_OPTIONS.map(opt => (
              <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="status-filter"
                  checked={pendingFilterSelections.status === opt.value}
                  onChange={() => handleOptionSelect("status", opt.value)}
                  className="form-radio h-4 w-4 text-blue-600"
                />
                <span className="text-xs font-medium">{opt.label}</span>
              </label>
            ))}
            {pendingFilterSelections.status && (
              <button
                className="text-xs text-gray-500 hover:underline mt-2 self-start"
                onClick={() => handleOptionSelect("status", "")}
                type="button"
              >
                Clear
              </button>
            )}
          </div>
        );
      case "program":
        return (
          <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
            {programs.filter((p: any) => p.programName.toLowerCase().includes((searchInputs.program || "").toLowerCase())).map((program: any) => (
              <label key={program.programCode} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="program-filter"
                  checked={pendingFilterSelections.program === program.programCode}
                  onChange={() => handleOptionSelect("program", program.programCode)}
                  className="form-radio h-4 w-4 text-blue-600"
                />
                <span className="text-xs font-medium">{program.programName}</span>
              </label>
            ))}
            {pendingFilterSelections.program && (
              <button
                className="text-xs text-gray-500 hover:underline mt-2 self-start"
                onClick={() => handleOptionSelect("program", "")}
                type="button"
              >
                Clear
              </button>
            )}
          </div>
        );
      case "merchant":
        return (
          <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
            {merchants.filter((m: any) => m.merchantName.toLowerCase().includes((searchInputs.merchant || "").toLowerCase())).map((merchant: any) => (
              <label key={merchant.merchantCode} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="merchant-filter"
                  checked={pendingFilterSelections.merchant === merchant.merchantCode}
                  onChange={() => handleOptionSelect("merchant", merchant.merchantCode)}
                  className="form-radio h-4 w-4 text-blue-600"
                />
                <span className="text-xs font-medium">{merchant.merchantName}</span>
              </label>
            ))}
            {pendingFilterSelections.merchant && (
              <button
                className="text-xs text-gray-500 hover:underline mt-2 self-start"
                onClick={() => handleOptionSelect("merchant", "")}
                type="button"
              >
                Clear
              </button>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Card className="mb-6 border-0 shadow-sm">
      <CardContent className="p-4">
        <div className="flex flex-wrap items-center justify-between mb-2 gap-3">
          {/* Left: Search dropdown, input, date selector */}
          <div className="flex gap-3 items-center">
            <Select value={searchType} onValueChange={setSearchType}>
              <SelectTrigger className="h-10 w-56">
                <SelectValue placeholder="Order ID" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="orderId">Order ID</SelectItem>
                <SelectItem value="memberId">Member ID</SelectItem>
              </SelectContent>
            </Select>
            <Input
              className="w-64"
              type="text"
              placeholder={searchType === "orderId" ? "Search by Order ID" : "Search by Member ID"}
              value={searchTerm}
              onChange={e => onSearch(e.target.value)}
            />
            <Select onValueChange={v => { onDateRangeChange && onDateRangeChange(v); }} value={dateRange}>
              <SelectTrigger className="h-10 w-40">
                <SelectValue placeholder="Choose Date" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Choose Date</SelectItem>
                <SelectItem value="30days">Last 30 Days</SelectItem>
                <SelectItem value="7days">Last 7 Days</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="yesterday">Yesterday</SelectItem>
                <SelectItem value="thisMonth">This Month</SelectItem>
                <SelectItem value="lastMonth">Last Month</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* Right: Filter, Reset, Export */}
          <div className="flex gap-2 items-center ml-auto">
            <Popover open={filterPopoverOpen} onOpenChange={setFilterPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant={activeFilterCount > 0 ? "default" : "outline"}
                  className="flex items-center gap-2 px-3 py-2 text-sm"
                  onClick={() => setFilterPopoverOpen(true)}
                  size="sm"
                >
                  <Filter className="h-4 w-4" />
                  Filter
                  {activeFilterCount > 0 && (
                    <span className="ml-1 bg-blue-600 text-white rounded-full px-2 py-0.5 text-xs font-semibold">{activeFilterCount}</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[340px] p-0" align="end">
                <div className="flex max-h-[320px] h-[320px]">
                  {/* Left: Categories */}
                  <div className="w-32 border-r bg-gray-50 p-2 flex flex-col gap-1">
                    {FILTER_CATEGORIES.map(cat => (
                      <button
                        key={cat.value}
                        className={`text-left px-2 py-1 rounded hover:bg-gray-100 font-medium text-xs ${activeCategory === cat.value ? "bg-white shadow text-blue-600" : "text-gray-700"}`}
                        onClick={() => setActiveCategory(cat.value)}
                      >
                        {cat.label}
                        {cat.isArray && Array.isArray(filterSelections[cat.value]) && filterSelections[cat.value].length > 0 ? (
                          <span className="ml-2 bg-blue-100 text-blue-700 rounded-full px-1.5 py-0.5 text-[10px] font-semibold">{filterSelections[cat.value].length}</span>
                        ) : null}
                      </button>
                    ))}
                    <div className="flex-1" />
                    <button
                      className="text-[10px] text-gray-500 hover:underline mt-2"
                      onClick={handleClearAll}
                      type="button"
                    >
                      Clear all
                    </button>
                  </div>
                  {/* Right: Options for selected category */}
                  <div className="flex-1 p-2 flex flex-col min-h-0">
                    <div className="flex items-center mb-1">
                      {activeCategory !== 'status' && (
                        <Input
                          placeholder={`Search ${activeCategory}`}
                          value={searchInputs[activeCategory] || ""}
                          onChange={e => handleSearchInput(activeCategory, e.target.value)}
                          className="h-7 w-full text-xs"
                        />
                      )}
                      {activeCategory !== 'status' && Array.isArray(filterSelections[activeCategory]) && filterSelections[activeCategory].length > 0 && (
                        <button
                          className="ml-2 text-[10px] text-gray-500 hover:underline"
                          onClick={handleClearCurrent}
                          type="button"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    <div className="text-xs font-medium flex-1 min-h-0 overflow-y-auto gap-1 flex flex-col">
                      {renderOptions()}
                    </div>
                    <div className="flex justify-end mt-2">
                      <Button
                        size="sm"
                        className="text-xs px-3 py-1 h-7"
                        onClick={applyFilters}
                      >
                        Apply
                      </Button>
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-2 px-3 py-2 text-sm"
              onClick={() => {
                handleClearAll();
                if (onReset) onReset();
              }}
            >
              <RotateCcw className="h-4 w-4" />
              Reset
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2 px-3 py-2 text-sm"
                >
                  <Download className="h-4 w-4" />
                  Export
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-2" align="end">
                <div className="flex flex-col gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="justify-start text-sm"
                    onClick={() => onExport && onExport("csv")}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    <span>CSV</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="justify-start text-sm"
                    onClick={() => onExport && onExport("pdf")}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    <span>PDF</span>
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}; 